/**
 * The building blocks of a Loan Drilldown modal / sidebar form — the
 * primitives FORM_STYLE_GUIDE.md describes, wearing `theme/formStyles.js`.
 * Lifted out of `ExperienceItemDialog` once the DSCR source drawers became
 * the second and third forms on the system.
 *
 * Every form that opens over the drilldown builds from these — a form never
 * restyles a section header, a field, a chip, a warning or a document row of
 * its own. If a form needs a part that isn't here, add it HERE (and a section
 * to the guide) before using it.
 *
 *   FormSection / FormSectionDivider   badge + title, helper below; 40px hairline
 *   FormField                          label-above InputWrapper (required marker)
 *   FormTextField / FormMoneyField     the two everyday controls, 40px / 14px
 *   FormPhoneField / FormSsnField      format on screen, store digits
 *   FormSelectField / FormComboField   a menu · the same with type-ahead
 *   FormOptionChips                    one chip for single- and multi-select
 *   FormChoiceCard                     a selectable card that carries data
 *   FormNote / FormWarn                quiet note · amber advisory
 *   FormDocumentGroup / FormDocumentRow  grouped 44px rows, name previews
 *   FormFileDrop                       drop zone + picked-file rows (the file IS the payload)
 *   FormVerifiedToggle                 the verified / candidate banner
 *   FormFooter                         status left · actions right (dialog or sticky)
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import InsertDriveFileOutlined from "@mui/icons-material/InsertDriveFileOutlined";
import VerifiedOutlined from "@mui/icons-material/VerifiedOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import Txt from "../typography/Txt";
import AddressGeolocateInput from "../inputs/AddressGeolocateInput";
import GoogleAddressInput from "../inputs/GoogleAddressInput";
import DollarInput from "../inputs/DollarInput";
import PercentInput from "../inputs/PercentInput";
import InputWrapper from "../inputs/shared/InputWrapper";
import { filePreviewSetData } from "../../redux/reducers/filePreviewReducer";
import { losPipelineApi } from "../../api/losPipelineApi";
import { formatPhone } from "../../_src_shared/utils/functions/formatPhone";
import { status } from "../../theme/colors";
import { FIELD_TEXT, FORM, formLabel, formSx, px } from "../../theme/formStyles";

/* ───────────────────────────── sections ────────────────────────────────── */

/**
 * Badge + title on one line, helper on the next; children indent to the title.
 *
 * Collapsible when `collapsible` — the head becomes the toggle (chevron at the
 * right, `summary` beside it while closed so a collapsed section still says
 * what's in it). `defaultCollapsed` sets the initial state only: a form that
 * opens an already-complete record collapses its finished sections so the work
 * left to do is what's on screen, and the user can still open any of them.
 *
 * **Sections are expanded by default, and stay that way unless a form opts
 * one out.** `complete` is the form saying "this section has nothing left to
 * ask for"; only then does `collapseWhenComplete` (default ON) fold it at
 * mount. There is no inference from `count` — the form states the rule per
 * section (all inputs filled? only the required ones?), because a guessed
 * rule folds sections nobody has touched.
 *
 * It is an INITIAL state, not a binding: filling the last field never snaps a
 * section shut under the cursor. For that beat, use `autoCollapseWhen`.
 */
export const FormSection = ({
  n,
  title,
  helper,
  children,
  collapsible,
  defaultCollapsed,
  autoCollapseWhen,
  summary,
  count,
  complete,
  collapseWhenComplete = true,
  block,
}) => {
  // Initial state only — read once, so later edits can't close the section.
  const [open, setOpen] = useState(
    () => !(defaultCollapsed || (collapsible && collapseWhenComplete && complete === true)),
  );

  // `autoCollapseWhen` closes the section the FIRST time it becomes complete
  // — the "that's done, move on" beat while filling a new record. It fires
  // once per completion: reopening stays open while you edit, and it re-arms
  // only if the section goes incomplete again.
  const wasComplete = useRef(!!autoCollapseWhen);
  useEffect(() => {
    if (!collapsible || autoCollapseWhen == null) return;
    if (autoCollapseWhen && !wasComplete.current) setOpen(false);
    wasComplete.current = !!autoCollapseWhen;
  }, [autoCollapseWhen, collapsible]);
  // Head → body: 24px open, NOTHING collapsed. Set on the head itself, not as
  // a `& > *` reset from the toggle — two emotion classes of equal specificity
  // fight, and which one wins depends on injection order.
  const showBody = !collapsible || open;
  const head = (
    <Box sx={{ ...formSx.sectionHead, ...formSx.sectionHeadGap(showBody) }}>
      {n != null ? <Box sx={formSx.sectionBadge}>{n}</Box> : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: px(FORM.s.s2),
            minWidth: 0,
          }}
        >
          <Txt sx={formSx.sectionTitle}>{title}</Txt>
          {count ? (
            <Box sx={formSx.sectionCount(count.filled >= count.total)}>
              {count.filled}/{count.total}
            </Box>
          ) : null}
        </Box>
        {helper && (open || !summary) ? (
          <Txt sx={formSx.sectionHelper}>{helper}</Txt>
        ) : null}
        {!open && summary ? (
          <Txt sx={formSx.sectionHelper}>{summary}</Txt>
        ) : null}
      </Box>
      {collapsible ? (
        <ExpandMoreRounded
          sx={{
            fontSize: 20,
            color: FORM.color.ink4,
            flexShrink: 0,
            transition: "transform .15s",
            transform: open ? "rotate(180deg)" : "none",
          }}
        />
      ) : null}
    </Box>
  );
  return (
    <Box sx={block ? formSx.panelSection : undefined}>
      {collapsible ? (
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          sx={{
            width: "100%",
            background: "none",
            border: 0,
            p: 0,
            cursor: "pointer",
            textAlign: "left",
            fontFamily: "inherit",
            "&:hover .MuiTypography-root": { color: FORM.color.ink },
          }}
        >
          {head}
        </Box>
      ) : (
        head
      )}
      <Collapse in={!collapsible || open} unmountOnExit={false}>
        <Box sx={n != null ? formSx.sectionBody : undefined}>{children}</Box>
      </Collapse>
    </Box>
  );
};

FormSection.propTypes = {
  n: PropTypes.number,
  title: PropTypes.node,
  helper: PropTypes.node,
  children: PropTypes.node,
  /** Head toggles the body. */
  collapsible: PropTypes.bool,
  /** Start closed — use for a section whose fields are already complete. */
  defaultCollapsed: PropTypes.bool,
  /** Closes the section the first time this turns true (create-time "done" beat). */
  autoCollapseWhen: PropTypes.bool,
  /** One line shown in place of the helper while collapsed. */
  summary: PropTypes.node,
  /** `{ filled, total }` → the n/total pill beside the title; brand when done. */
  count: PropTypes.shape({ filled: PropTypes.number, total: PropTypes.number }),
  /**
   * This section has nothing left to ask for, by whatever rule the FORM
   * chose (all inputs filled / only the required ones). Never inferred — a
   * section with no `complete` stays open. Read at mount only: it decides the
   * STARTING state, nothing after.
   */
  complete: PropTypes.bool,
  /** Start a `complete` section collapsed. On by default; pass false to opt out. */
  collapseWhenComplete: PropTypes.bool,
  /** Wrap in the panel-form section block (32px padding + top hairline). */
  block: PropTypes.bool,
};

export const FormSectionDivider = () => <Divider sx={formSx.divider} />;

/* ───────────────────────────── fields ──────────────────────────────────── */

/**
 * Label-above field: `InputWrapper` wearing the form system's label + helper.
 *
 * `span` (6 / 3 / 2) places it in a `FormFieldGrid`; `optional` marks a field
 * the form does not count — required fields carry the asterisk, everything
 * else is silent EXCEPT where a reader would otherwise assume it's needed
 * (FORM_STYLE_GUIDE.md §4).
 */
/**
 * Which label / helper type a FormField renders. "standard" is the FORM_STYLE_GUIDE
 * recipe (13/500 ink label, 12.5 helper). "field" is the 2026-10-08 standard
 * (FIELD_TEXT — 12.5px #111827 label 4px above the control, 11.5px #64748B helper)
 * that every NEW form uses: wrap the fields in <FormFieldTextProvider> (modal
 * forms get it from QuickEditDialog's `fieldText="field"`).
 */
const FormFieldTextContext = createContext("standard");

export const FormFieldTextProvider = ({ value = "field", children }) => (
  <FormFieldTextContext.Provider value={value}>
    {children}
  </FormFieldTextContext.Provider>
);

FormFieldTextProvider.propTypes = {
  value: PropTypes.oneOf(["standard", "field"]),
  children: PropTypes.node,
};

export const FormField = ({
  label,
  required,
  optional,
  helperText,
  span,
  children,
}) => {
  const fieldText = useContext(FormFieldTextContext) === "field";
  const field = (
    <InputWrapper
      label={
        <>
          {formLabel(label, { required })}
          {optional ? (
            <Box component="span" sx={formSx.labelOptional}>
              (optional)
            </Box>
          ) : null}
        </>
      }
      labelSx={fieldText ? FIELD_TEXT.label : formSx.label}
      helperText={helperText}
      helperSx={fieldText ? FIELD_TEXT.helper : formSx.helper}
    >
      {children}
    </InputWrapper>
  );
  return span ? <Box sx={formSx.fieldSpan(span)}>{field}</Box> : field;
};

FormField.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  helperText: PropTypes.node,
  span: PropTypes.number,
  children: PropTypes.node,
};

/**
 * The 6-column field grid: full = 6, half = 3, third = 2 (§4.3). Under the
 * field standard (FormFieldTextProvider "field") rows sit 24px apart.
 */
export const FormFieldGrid = ({ children }) => {
  const fieldText = useContext(FormFieldTextContext) === "field";
  return (
    <Box sx={{ ...formSx.fieldGrid, ...(fieldText ? FIELD_TEXT.grid : null) }}>
      {children}
    </Box>
  );
};

FormFieldGrid.propTypes = { children: PropTypes.node };

export const FormTextField = ({
  label,
  value,
  onChange,
  placeholder,
  helperText,
  type,
  required,
  optional,
  span,
  suffix,
  multiline,
  minRows,
  disabled,
  endAdornment,
  inputProps,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <TextField
      size="small"
      fullWidth
      type={type}
      inputProps={inputProps}
      multiline={multiline}
      minRows={minRows}
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      InputProps={
        suffix
          ? {
              endAdornment: (
                <Box component="span" sx={formSx.inputSuffix}>
                  {suffix}
                </Box>
              ),
            }
          : endAdornment
            ? { endAdornment }
            : undefined
      }
      sx={
        multiline
          ? {
              ...formSx.input,
              "& .MuiOutlinedInput-root": {
                fontSize: FORM.type.body[0],
                borderRadius: px(FORM.radius.control),
              },
            }
          : formSx.input
      }
    />
  </FormField>
);

FormTextField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  type: PropTypes.string,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  /** Quiet unit inside the control's right edge — "years", "projects". */
  suffix: PropTypes.node,
  multiline: PropTypes.bool,
  minRows: PropTypes.number,
  disabled: PropTypes.bool,
  endAdornment: PropTypes.node,
  /** Attributes for the native <input> (e.g. a date input's `max`). */
  inputProps: PropTypes.object,
};

export const FormMoneyField = ({
  label,
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  disabled,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <DollarInput
      value={value ?? ""}
      onChange={(v) => {
        if (typeof v === "number" || v == null) onChange(v);
      }}
      size="small"
      placeholder="0.00"
      disabled={disabled}
      sx={formSx.input}
    />
  </FormField>
);

FormMoneyField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/**
 * Percent field. Wraps `PercentInput`, so the STORED value keeps the contract
 * the RFF forms wrote: a fraction (0.85), not 85. The control shows the
 * percent; only the display is scaled.
 */
export const FormPercentField = ({
  label,
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  disabled,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <PercentInput
      value={value ?? ""}
      onChange={onChange}
      size="small"
      disabled={disabled}
      sx={formSx.input}
    />
  </FormField>
);

FormPercentField.propTypes = {
  label: PropTypes.node,
  /** A FRACTION — 0.85 for 85%. */
  value: PropTypes.any,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/**
 * Phone field — the SAME contract `RffPhoneField` has carried since the RFF
 * forms: the control shows `(555) 555-0123`, the VALUE is digits only.
 *
 * Splitting those two matters. Every surface that renders a stored phone runs
 * it back through `formatPhone`, so a record holding `"(555) 555-0123"`, one
 * holding `"555.555.0123"` and one holding `5555550123` all have to survive the
 * round trip — which they only do if what we write is the bare digits.
 */
export const FormPhoneField = ({
  label,
  value,
  onChange,
  helperText,
  placeholder = "(555) 555-5555",
  required,
  optional,
  span,
  disabled,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <TextField
      size="small"
      fullWidth
      type="tel"
      disabled={disabled}
      value={formatPhone(value)}
      onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ""))}
      placeholder={placeholder}
      sx={formSx.input}
    />
  </FormField>
);

FormPhoneField.propTypes = {
  label: PropTypes.node,
  /** Digits, or any stored phone string — the control formats it for display. */
  value: PropTypes.any,
  /** Called with digits only. */
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/**
 * Social security number. Stores DIGITS ONLY (max 9) and shows `123-45-6789`;
 * masked by default with a reveal button, because a form sits open on a shared
 * screen and an SSN is the one field on it nobody else needs to read.
 */
const formatSsn = (digits) => {
  const d = String(digits ?? "").replace(/\D/g, "");
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

export const FormSsnField = ({
  label = "Social security number",
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  disabled,
}) => {
  const [shown, setShown] = useState(false);
  const digits = String(value ?? "").replace(/\D/g, "");
  return (
    <FormField
      label={label}
      required={required}
      optional={optional}
      span={span}
      helperText={helperText}
    >
      <TextField
        size="small"
        fullWidth
        // Masked: render the raw digits through a password field, so the
        // formatting dashes don't leak the length of what's typed.
        type={shown ? "text" : "password"}
        disabled={disabled}
        value={shown ? formatSsn(digits) : digits}
        onChange={(e) =>
          onChange(e.target.value.replace(/\D/g, "").slice(0, 9))
        }
        placeholder={shown ? "123-45-6789" : undefined}
        sx={formSx.input}
        InputProps={{
          endAdornment: (
            <IconButton
              size="small"
              edge="end"
              tabIndex={-1}
              aria-label={shown ? "Hide SSN" : "Show SSN"}
              onClick={() => setShown((v) => !v)}
              sx={{ color: FORM.color.ink4 }}
            >
              {shown ? (
                <VisibilityOffOutlined sx={{ fontSize: 18 }} />
              ) : (
                <VisibilityOutlined sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          ),
        }}
      />
    </FormField>
  );
};

FormSsnField.propTypes = {
  label: PropTypes.node,
  /** Digits, or any stored SSN string. */
  value: PropTypes.any,
  /** Called with digits only, capped at 9. */
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/* ───────────────────────────── toggles ─────────────────────────────────── */

/**
 * One chip for single- and multi-select. `options` are strings or
 * `{ value, label, tag }`; `value` is a string (single) or an array (multi).
 * Clicking a selected single-select chip clears it.
 */
export const FormOptionChips = ({
  options,
  value,
  onChange,
  multiple = false,
}) => {
  const selected = multiple
    ? Array.isArray(value)
      ? value.map(String)
      : []
    : value;
  return (
    <Box sx={formSx.chipRow}>
      {options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        const on = multiple
          ? selected.includes(String(opt.value))
          : selected === opt.value;
        const next = () => {
          if (multiple)
            onChange(
              on
                ? selected.filter((v) => v !== String(opt.value))
                : [...selected, String(opt.value)],
            );
          else onChange(on ? null : opt.value);
        };
        return (
          <Chip
            key={String(opt.value)}
            label={
              opt.tag ? (
                <>
                  {opt.label}
                  <Box component="span" sx={formSx.chipTag(on)}>
                    {opt.tag}
                  </Box>
                </>
              ) : (
                opt.label
              )
            }
            icon={on ? <CheckRounded /> : undefined}
            onClick={opt.disabled ? undefined : next}
            disabled={opt.disabled}
            aria-pressed={on}
            sx={formSx.chip(on)}
          />
        );
      })}
    </Box>
  );
};

FormOptionChips.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
};

/** A selectable card that carries data the user compares before picking. */
export const FormChoiceCard = ({
  selected,
  disabled,
  onClick,
  children,
  sx,
}) => (
  <Box
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-pressed={!!selected}
    aria-disabled={!!disabled}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(e) => {
      if (!disabled && (e.key === "Enter" || e.key === " ")) {
        e.preventDefault();
        onClick?.();
      }
    }}
    sx={{ ...formSx.choice(selected, disabled), ...sx }}
  >
    {children}
  </Box>
);

FormChoiceCard.propTypes = {
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.object,
};

export const FormPill = ({ tone = "neutral", children }) => (
  <Box component="span" sx={formSx.pill(tone)}>
    {children}
  </Box>
);

FormPill.propTypes = { tone: PropTypes.string, children: PropTypes.node };

/* ───────────────────────────── messages ────────────────────────────────── */

export const FormNote = ({ children, sx }) => (
  <Box sx={{ ...formSx.note, ...sx }}>{children}</Box>
);

FormNote.propTypes = { children: PropTypes.node, sx: PropTypes.object };

/** Amber advisory. Copy: bold problem · plain consequence · imperative fix. */
export const FormWarn = ({ children, action }) => (
  <Box sx={formSx.warn}>
    <WarningAmberRounded />
    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
      {children}
    </Box>
    {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
  </Box>
);

FormWarn.propTypes = { children: PropTypes.node, action: PropTypes.node };

/** Teal context callout: how the thing works, not a problem (§9). */
export const FormInfo = ({ children }) => (
  <Box sx={formSx.info}>
    <InfoOutlined />
    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
      {children}
    </Box>
  </Box>
);

FormInfo.propTypes = { children: PropTypes.node };

/* ─────────────────────────── document rows ─────────────────────────────── */

const docDisplayName = (d) =>
  d?.file_display_name || d?.fileName || d?.docType || "Document";

/**
 * One 44px document row: whole row is the checkbox's label; the file NAME
 * previews the document (always clickable — CLAUDE.md) without toggling.
 */
export const FormDocumentRow = ({ doc, checked, onToggle, meta }) => {
  const dispatch = useDispatch();
  return (
    <Box component="label" sx={formSx.listRow(checked)}>
      <Checkbox
        size="small"
        checked={!!checked}
        onChange={() => onToggle(doc._id)}
        sx={formSx.checkbox}
      />
      <Txt
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          dispatch(filePreviewSetData(doc));
        }}
        sx={{
          ...formSx.listRowName,
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {docDisplayName(doc)}
      </Txt>
      <Txt sx={formSx.listRowMeta}>
        {meta ?? (doc?.docType || doc?.docGroup || "")}
      </Txt>
    </Box>
  );
};

FormDocumentRow.propTypes = {
  doc: PropTypes.object,
  checked: PropTypes.bool,
  onToggle: PropTypes.func,
  meta: PropTypes.node,
};

/** Grouped container: head (title · count · optional pill) + rows. */
export const FormDocumentGroup = ({
  title,
  pill,
  docs,
  selectedIds,
  onToggle,
  metaOf,
}) => (
  <Box sx={formSx.listGroup}>
    <Box sx={formSx.listGroupHead}>
      {title}
      <Box component="span" sx={formSx.listGroupCount}>
        {docs.length}
      </Box>
      {pill ? (
        <Box component="span" sx={formSx.listGroupPill}>
          {pill}
        </Box>
      ) : null}
    </Box>
    {docs.map((d) => (
      <FormDocumentRow
        key={d._id}
        doc={d}
        checked={selectedIds.includes(String(d._id))}
        onToggle={onToggle}
        meta={metaOf ? metaOf(d) : undefined}
      />
    ))}
  </Box>
);

FormDocumentGroup.propTypes = {
  title: PropTypes.node,
  pill: PropTypes.node,
  docs: PropTypes.array,
  selectedIds: PropTypes.array,
  onToggle: PropTypes.func,
  metaOf: PropTypes.func,
};

export { docDisplayName };

/* ──────────────────────────── file drop zone ───────────────────────────── */

const fileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * A local, not-yet-uploaded file has no loan document to hand the preview
 * sidebar, so its name opens the browser's own viewer on a blob URL. Revoked
 * after a minute — long enough for the new tab to load it.
 */
const previewLocalFile = (file) => {
  if (!(file instanceof Blob)) return;
  const url = URL.createObjectURL(file);
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

/**
 * The upload control for a form whose FILE is the payload (§10b). The value is
 * the upload-metadata array `useAxiosHook` turns into multipart —
 * `[{ id, file, name, file_display_name, docGroup, docType }]`, the same shape
 * `RffFileUploadField` wrote — so a converted form posts what its RFF version
 * did. `multiple={false}` (the default) keeps one file: a second drop
 * replaces it, and the zone gives way to the file row once one is picked.
 */
export const FormFileDrop = ({
  label,
  required,
  optional,
  helperText,
  span,
  value,
  onChange,
  multiple = false,
  accept,
  docGroup,
  docType,
  prompt = "Drop a file here or",
  hint,
}) => {
  const inputRef = useRef(null);
  const depth = useRef(0);
  const [over, setOver] = useState(false);
  const files = Array.isArray(value) ? value : [];

  const add = (list) => {
    const picked = Array.from(list || []);
    if (!picked.length) return;
    const entries = (multiple ? picked : picked.slice(0, 1)).map((file) => ({
      id: uuidv4(),
      file,
      name: file.name,
      file_display_name: file.name,
      docGroup: docGroup ?? null,
      docType: docType ?? null,
    }));
    onChange(multiple ? [...files, ...entries] : entries);
  };

  const drag = (delta) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    depth.current = Math.max(0, depth.current + delta);
    setOver(depth.current > 0);
  };

  const showZone = multiple || files.length === 0;

  return (
    <FormField
      label={label}
      required={required}
      optional={optional}
      helperText={helperText}
      span={span}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: px(FORM.s.s2) }}>
        {files.length ? (
          <Box sx={formSx.listGroup}>
            {files.map((f) => (
              <Box key={f.id} sx={formSx.fileRow}>
                <InsertDriveFileOutlined />
                <Txt
                  role="button"
                  tabIndex={0}
                  onClick={() => previewLocalFile(f.file)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") previewLocalFile(f.file);
                  }}
                  sx={{
                    ...formSx.listRowName,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {f.file_display_name || f.name}
                </Txt>
                <Txt sx={formSx.listRowMeta}>{fileSize(f.file?.size)}</Txt>
                <IconButton
                  size="small"
                  aria-label={`Remove ${f.file_display_name || f.name}`}
                  onClick={() => onChange(files.filter((x) => x !== f))}
                >
                  <CloseRounded fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : null}

        {showZone ? (
          // A div acting as a button, NOT a <button>: some browsers never
          // deliver dragover/drop to a <button>, so dropping a file on it did
          // nothing while clicking worked.
          <Box
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={drag(1)}
            onDragLeave={drag(-1)}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              depth.current = 0;
              setOver(false);
              add(e.dataTransfer?.files);
            }}
            sx={formSx.drop(over)}
          >
            <Box sx={formSx.dropIcon(over)}>
              <FileUploadOutlined />
            </Box>
            <Txt sx={formSx.dropText}>
              {prompt} <strong>browse</strong>
            </Txt>
            {hint ? <Txt sx={formSx.dropHint}>{hint}</Txt> : null}
          </Box>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          hidden
          multiple={multiple}
          accept={accept}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
        />
      </Box>
    </FormField>
  );
};

FormFileDrop.propTypes = {
  label: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  helperText: PropTypes.node,
  span: PropTypes.number,
  /** The upload-metadata array (see above). */
  value: PropTypes.array,
  onChange: PropTypes.func,
  multiple: PropTypes.bool,
  accept: PropTypes.string,
  /** Stamped onto each entry — the backend files the upload under these. */
  docGroup: PropTypes.string,
  docType: PropTypes.string,
  prompt: PropTypes.node,
  hint: PropTypes.node,
};

/* ─────────────────────────── verified toggle ───────────────────────────── */

/**
 * The verified / candidate banner with its switch. `onChange` absent → read
 * only (the experience dialog verifies through a dedicated endpoint and
 * renders its own button in the `action` slot).
 */
export const FormVerifiedToggle = ({
  verified,
  onChange,
  verifiedText,
  candidateText,
  action,
}) => (
  <Box
    onClick={onChange ? () => onChange(!verified) : undefined}
    sx={{
      ...formSx.note,
      borderStyle: "solid",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: px(FORM.s.s3),
      cursor: onChange ? "pointer" : "default",
      bgcolor: verified ? status.success.bg : FORM.color.surface2,
      borderColor: verified ? "#A7F3D0" : FORM.color.divider,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: px(FORM.s.s3),
        flex: 1,
        minWidth: 0,
      }}
    >
      <VerifiedOutlined
        sx={{
          fontSize: 22,
          color: verified ? status.success.solid : FORM.color.ink4,
          flexShrink: 0,
        }}
      />
      <Box>
        <Txt sx={{ ...formSx.label, mb: 0, display: "block", fontWeight: 600 }}>
          {verified ? "Verified" : "Not yet verified"}
        </Txt>
        <Txt sx={{ ...formSx.helper, mt: 0, display: "block" }}>
          {verified ? verifiedText : candidateText}
        </Txt>
      </Box>
    </Box>
    {onChange ? (
      <Switch
        size="small"
        checked={!!verified}
        onChange={(e) => onChange(e.target.checked)}
        onClick={(e) => e.stopPropagation()}
      />
    ) : (
      action || null
    )}
  </Box>
);

FormVerifiedToggle.propTypes = {
  verified: PropTypes.bool,
  onChange: PropTypes.func,
  verifiedText: PropTypes.node,
  candidateText: PropTypes.node,
  action: PropTypes.node,
};

/* ─────────────────────────────── footer ────────────────────────────────── */

/** Status left, actions right. `sticky` for a sidebar body; dialog otherwise. */
export const FormFooter = ({ status: statusNode, children, sticky }) => (
  <Box sx={sticky ? formSx.stickyFoot : formSx.foot}>
    <Box sx={{ ...formSx.footStatus, minWidth: sticky ? 160 : 220 }}>
      {statusNode}
    </Box>
    <Box sx={formSx.buttonRow}>{children}</Box>
  </Box>
);

FormFooter.propTypes = {
  status: PropTypes.node,
  children: PropTypes.node,
  sticky: PropTypes.bool,
};

/* ─────────────────── panel-form controls (§12b) ────────────────────────── */

/** Single-choice select. Options are strings or `{ value, label }`. */
export const FormSelectField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  helperText,
  required,
  optional,
  span,
  disabled,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <TextField
      select
      size="small"
      fullWidth
      disabled={disabled}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      SelectProps={{
        displayEmpty: true,
        // Show the option's LABEL, not its stored value. Where the two differ
        // the value is a code — property condition stores "C4" and reads
        // "C4 — Adequately maintained…" — and the code alone is not an answer.
        renderValue: (v) => {
          if (!v)
            return (
              <Box component="span" sx={{ color: FORM.color.ink4 }}>
                {placeholder}
              </Box>
            );
          const match = options.find(
            (o) => (typeof o === "string" ? o : o.value) === v,
          );
          return typeof match === "string" ? match : (match?.label ?? v);
        },
      }}
      sx={formSx.input}
    >
      {options.map((o) => {
        const opt = typeof o === "string" ? { value: o, label: o } : o;
        return (
          <MenuItem
            key={String(opt.value)}
            value={opt.value}
            sx={{ fontSize: FORM.type.body[0] }}
          >
            {opt.label}
          </MenuItem>
        );
      })}
    </TextField>
  </FormField>
);

FormSelectField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.array,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/**
 * `FormSelectField` with type-ahead — same contract, same look, but the control
 * filters as you type instead of making you scroll a long menu. Reach for it
 * once a list passes roughly a dozen entries (loan roles, doc types, states).
 *
 * `freeSolo` is deliberately OFF and cannot be turned on: these lists are model
 * enums matched by string equality server-side, so a typed-but-unmatched value
 * is a WRONG value, not a new one (CLAUDE.md → *Select options come from the
 * BACKEND*). Typing narrows; only a pick commits.
 */
export const FormComboField = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  helperText,
  required,
  optional,
  span,
  disabled,
}) => {
  const opts = options.map((o) =>
    typeof o === "string" ? { value: o, label: o } : o,
  );
  const selected = opts.find((o) => o.value === value) || null;

  return (
    <FormField
      label={label}
      required={required}
      optional={optional}
      span={span}
      helperText={helperText}
    >
      <Autocomplete
        size="small"
        fullWidth
        disabled={disabled}
        options={opts}
        value={selected}
        onChange={(_, next) => onChange(next?.value ?? null)}
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(o, v) => o.value === v?.value}
        // The list is short enough to read whole once filtered, and an operator
        // who half-remembers a role ("estate") should still find it.
        autoHighlight
        openOnFocus
        renderInput={(params) => (
          // `formSx.input` verbatim — Autocomplete splits the plain field's
          // 14px inset between its root and its input, so the text still lands
          // on the same left edge as every other control in the grid.
          <TextField {...params} placeholder={placeholder} sx={formSx.input} />
        )}
        renderOption={(props, o) => (
          <Box
            component="li"
            {...props}
            key={String(o.value)}
            sx={{ fontSize: FORM.type.body[0] }}
          >
            {o.label}
          </Box>
        )}
        slotProps={{
          paper: { sx: { fontSize: FORM.type.body[0] } },
        }}
      />
    </FormField>
  );
};

FormComboField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  /** Strings, or `{ value, label }` — `value` must be the model's exact string. */
  options: PropTypes.array,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/** "#12345 · Smith Bridge Loan" — how a picked loan reads in the field. */
const loanLabel = (l) =>
  l
    ? [l.loanNumber != null ? `#${l.loanNumber}` : null, l.loanName].filter(Boolean).join(" · ") ||
      "Loan"
    : "";

/**
 * Loan picker — type to search loans across both pipelines (the header Loan
 * Search endpoint, `GET /underwriting/loan_search`, 2+ characters), pick one.
 * Same look as `FormComboField`. The value is a slim loan:
 * `{ id, loanNumber, loanName, link }` — `id` is the Mongo `_id` and `link` the
 * deep link the endpoint returns, so a caller links by `_id`, never by number
 * (CLAUDE.md → *Loan URLs are keyed by `_id`*). A superseded query is aborted.
 */
export const FormLoanSearchField = ({
  label = "Loan",
  value,
  onChange,
  placeholder = "Search by loan number, name, borrower or address",
  helperText,
  required,
  optional,
  span,
  disabled,
}) => {
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = input.trim();
    if (q.length < 2 || q === loanLabel(value)) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      losPipelineApi
        .searchLoans({ q, limit: 12, signal: controller.signal })
        .then((rows) =>
          setOptions(
            rows.map((r) => ({
              id: r._id,
              loanNumber: r.loanNumber,
              loanName: r.loanName,
              link: r.link || `/loan/${r._id}`,
              loanStatus: r.loanStatus,
              pipeline: r.pipeline,
              address: r.addresses?.[0],
            })),
          ),
        )
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [input, value]);

  return (
    <FormField
      label={label}
      required={required}
      optional={optional}
      span={span}
      helperText={helperText}
    >
      <Autocomplete
        size="small"
        fullWidth
        disabled={disabled}
        options={options}
        value={value || null}
        loading={loading}
        filterOptions={(x) => x}
        onChange={(_, next) =>
          onChange(
            next
              ? { id: next.id, loanNumber: next.loanNumber, loanName: next.loanName, link: next.link }
              : null,
          )
        }
        inputValue={input}
        onInputChange={(_, v) => setInput(v)}
        getOptionLabel={loanLabel}
        isOptionEqualToValue={(o, v) => o.id === v?.id}
        noOptionsText={input.trim().length < 2 ? "Type at least 2 characters" : "No loans found"}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} sx={formSx.input} />
        )}
        renderOption={(props, o) => (
          <Box component="li" {...props} key={o.id} sx={{ display: "block !important" }}>
            <Txt component="div" sx={{ fontSize: FORM.type.body[0], color: FORM.color.ink }}>
              {loanLabel(o)}
            </Txt>
            <Txt component="div" sx={formSx.meta}>
              {[o.pipeline, o.loanStatus, o.address].filter(Boolean).join(" · ")}
            </Txt>
          </Box>
        )}
        slotProps={{ paper: { sx: { fontSize: FORM.type.body[0] } } }}
      />
    </FormField>
  );
};

FormLoanSearchField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.shape({
    id: PropTypes.string,
    loanNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    loanName: PropTypes.string,
    link: PropTypes.string,
  }),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/**
 * The "Loan or screen" field — search, recents, paste-a-link, and the linked
 * card it turns into. Lives in its own module (it is big); re-exported here so
 * forms keep importing every part from FormPrimitives. See LinkTargetField.js.
 */
export {
  LinkTargetField as FormLinkTargetField,
  LinkTargetCard as FormLinkTargetCard,
  linkTargetUrl,
  linkTargetTitle,
  linkTargetShort,
} from "./LinkTargetField";

/**
 * Date field. The CONTROL is a native `type="date"`; the VALUE keeps the LOS
 * contract the RFF date field writes — UTC-midnight ISO — so a converted form
 * posts exactly what the old one did, and the day can never slip a zone
 * (CLAUDE.md → *Dates & times*).
 */
const isoToDateInput = (v) => {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v))
    return v.slice(0, 10);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const dateInputToIso = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0)).toISOString();
};

/**
 * Draft state for a native `<input type="date">`. While the user types a year
 * the browser reports it digit by digit ("0002", "0020", "0202", "2026"), and
 * `Date.UTC` maps years 0–99 to 1900–1999 — committing those partials turned
 * "2" into 1902 and re-rendered the input with it, so a typed year always
 * landed in the 1900s. The draft holds the raw string; only a complete
 * four-digit year (or a cleared field) is committed upstream.
 *
 * The other end too: Chrome's year segment takes up to six digits, so typing
 * past "1992" gave "199222", which posted as the extended-ISO "+199222-…" and
 * then read back as an empty input. A year over 9999 is refused outright, and
 * every date input also carries `max={DATE_INPUT_MAX}` so the browser stops
 * the year segment at four digits.
 */
export const DATE_INPUT_MAX = "9999-12-31";

export const useDateInputDraft = (value, onChange) => {
  const external = isoToDateInput(value);
  const [draft, setDraft] = useState(external);
  useEffect(() => {
    setDraft(external);
  }, [external]);
  const handleChange = (s) => {
    if (!s) {
      setDraft(s);
      return onChange(null);
    }
    const y = Number(String(s).split("-")[0]);
    if (y > 9999) return;
    setDraft(s);
    if (y >= 1000) onChange(dateInputToIso(s));
  };
  return [draft, handleChange];
};

const FormDateInput = ({ value, onChange, ...rest }) => {
  const [draft, handleChange] = useDateInputDraft(value, onChange);
  return (
    <FormTextField
      {...rest}
      type="date"
      value={draft}
      onChange={handleChange}
      inputProps={{ max: DATE_INPUT_MAX }}
    />
  );
};

export const FormDateField = ({
  label,
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  disabled,
}) => (
  <FormDateInput
    label={label}
    value={value}
    onChange={onChange}
    helperText={helperText}
    required={required}
    optional={optional}
    span={span}
    disabled={disabled}
  />
);

FormDateField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  disabled: PropTypes.bool,
};

/** Address autocomplete, label-above, in the form system's input shape. */
export const FormAddressField = ({
  label = "Address",
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  placeholder = "Start typing an address",
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <AddressGeolocateInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      selectedVariant="input"
    />
  </FormField>
);

/**
 * Address via **Google Places** — the one to use for anything that needs the
 * county or real coordinates (a subject property feeds flood determination,
 * the property map and the sizer from them). `FormAddressField` above is the
 * older Azure-backed control; Azure Maps is frozen, so prefer this.
 *
 * `allowManualEntry` exposes the type-it-yourself override for raw land and
 * new subdivisions, where Places returns nothing useful.
 */
export const FormGoogleAddressField = ({
  label = "Address",
  value,
  onChange,
  helperText,
  required,
  optional,
  span,
  allowManualEntry,
  placeholder,
}) => (
  <FormField
    label={label}
    required={required}
    optional={optional}
    span={span}
    helperText={helperText}
  >
    <GoogleAddressInput
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      allowManualEntry={allowManualEntry}
    />
  </FormField>
);

FormGoogleAddressField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.object,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  allowManualEntry: PropTypes.bool,
  placeholder: PropTypes.string,
};

FormAddressField.propTypes = {
  label: PropTypes.node,
  value: PropTypes.object,
  onChange: PropTypes.func,
  helperText: PropTypes.node,
  required: PropTypes.bool,
  optional: PropTypes.bool,
  span: PropTypes.number,
  placeholder: PropTypes.string,
};

/**
 * Yes / No segmented control. Clicking the selected option clears the answer,
 * so "not answered yet" stays reachable — a boolean nobody has touched is
 * `null`, not `false`.
 */
export const FormSegmented = ({
  value,
  onChange,
  options = [
    { value: true, label: "Yes", accent: true },
    { value: false, label: "No" },
  ],
  ariaLabel,
}) => (
  <Box sx={formSx.segTrack} role="group" aria-label={ariaLabel}>
    {options.map((o) => {
      const on = value === o.value;
      return (
        <Box
          key={String(o.value)}
          component="button"
          type="button"
          aria-pressed={on}
          onClick={() => onChange(on ? null : o.value)}
          sx={formSx.segOption(on, o.accent)}
        >
          {o.label}
        </Box>
      );
    })}
  </Box>
);

FormSegmented.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.array,
  ariaLabel: PropTypes.string,
};

/* ─────────────── confirmation list (title · subtext · control) ─────────── */

/**
 * A bordered list of statements answered in place — each row is a title, an
 * optional line saying who the answer is for or what it triggers, and ONE
 * control on the right.
 *
 * `variant`: "switch" (default — an on/off fact) or "segmented" (Yes / No /
 * unanswered, for when "not answered" has to stay distinguishable).
 */
export const FormCheckRow = ({
  title,
  subtext,
  value,
  onChange,
  variant = "switch",
  control,
}) => (
  <Box
    component={variant === "switch" && !control ? "label" : "div"}
    sx={formSx.checkRow}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Txt sx={formSx.checkRowTitle}>{title}</Txt>
      {subtext ? <Txt sx={formSx.checkRowSub}>{subtext}</Txt> : null}
    </Box>
    {control ??
      (variant === "segmented" ? (
        <FormSegmented value={value} onChange={onChange} ariaLabel={title} />
      ) : (
        <Switch
          size="small"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{
            "aria-label": typeof title === "string" ? title : undefined,
          }}
          sx={{ flexShrink: 0 }}
        />
      ))}
  </Box>
);

FormCheckRow.propTypes = {
  title: PropTypes.node,
  subtext: PropTypes.node,
  value: PropTypes.any,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(["switch", "segmented"]),
  control: PropTypes.node,
};

export const FormCheckList = ({ children }) => (
  <Box sx={formSx.checkList}>{children}</Box>
);

FormCheckList.propTypes = { children: PropTypes.node };

/* ──────────────────────── method selector cards ────────────────────────── */

/**
 * "How would you like to add this?" — the create-path chooser. `options` are
 * `{ value, title, description, icon }`; the picked card carries the accent
 * border, the tint and a filled radio dot.
 *
 * `layout="inline"` is the compact picker: icon + title on one line, the
 * description under both, no radio dot; an option's `tone: { bg, fg }` tints
 * its icon tile.
 */
export const FormMethodCards = ({
  question,
  options = [],
  value,
  onChange,
  layout = "stacked",
}) => (
  <Box>
    {question ? <Txt sx={formSx.methodQuestion}>{question}</Txt> : null}
    <Box
      sx={formSx.methodGrid(options.length)}
      role="radiogroup"
      aria-label={question}
    >
      {options.map((o) => {
        const on = value === o.value;
        return (
          <Box
            key={String(o.value)}
            component="button"
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            sx={{
              ...formSx.methodCard(on),
              ...(layout === "inline" && formSx.methodCardInline),
            }}
          >
            {layout === "inline" ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: px(FORM.s.s2) }}>
                  <Box
                    sx={{
                      ...formSx.methodIconInline,
                      ...(o.tone && { bgcolor: o.tone.bg, color: o.tone.fg }),
                    }}
                  >
                    {o.icon}
                  </Box>
                  <Txt sx={formSx.methodTitle}>{o.title}</Txt>
                </Box>
                {o.description ? <Txt sx={formSx.methodDesc}>{o.description}</Txt> : null}
              </>
            ) : (
              <>
                <Box sx={formSx.methodIcon(on)}>{o.icon}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Txt sx={formSx.methodTitle}>{o.title}</Txt>
                  {o.description ? (
                    <Txt sx={formSx.methodDesc}>{o.description}</Txt>
                  ) : null}
                </Box>
                <Box sx={formSx.methodRadio(on)} />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  </Box>
);

FormMethodCards.propTypes = {
  layout: PropTypes.oneOf(["stacked", "inline"]),
  question: PropTypes.node,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
};

/* ──────────────────────────── panel footer ─────────────────────────────── */

/**
 * The PINNED footer of a sidebar form — pass it to `DrilldownSidebarPane`'s
 * `footerContent`, never inside the body (the body is the only scroller).
 *
 * Left: how much is left, as a 96px bar plus one line — "3 required fields
 * left", then "Ready to save". It counts; it does NOT name the missing fields
 * (the asterisks already do — FORM_STYLE_GUIDE.md §11). Right: the actions.
 */
export const FormPanelFooter = ({
  required = 0,
  filled = 0,
  readyText = "Ready to save",
  children,
}) => {
  const left = Math.max(0, required - filled);
  const pct = required
    ? Math.round((Math.min(filled, required) / required) * 100)
    : 100;
  return (
    <Box sx={formSx.panelFoot}>
      <Box sx={formSx.progressRow}>
        <Box sx={formSx.progressTrack}>
          <Box sx={formSx.progressFill(pct)} />
        </Box>
        <Txt sx={formSx.progressText}>
          {left === 0
            ? readyText
            : `${left} required field${left === 1 ? "" : "s"} left`}
        </Txt>
      </Box>
      <Box sx={formSx.buttonRow}>{children}</Box>
    </Box>
  );
};

FormPanelFooter.propTypes = {
  /** How many required fields the form has. */
  required: PropTypes.number,
  /** How many of them are filled. */
  filled: PropTypes.number,
  readyText: PropTypes.string,
  children: PropTypes.node,
};
